import { GLOBAL } from 'saddlebag-browser';

import type {
  KetchTCFFeature,
  KetchTCFSpecialPurpose,
  KetchVendor,
  RawKetchPurpose,
  RawKetchVendor,
  RawTCFSystemVendor,
  RawTCFVendorMetadata,
} from './ketch-types';

// Type for Ketch SDK function (used by ketch-consent-api.ts)
type KetchFunction = (command: string, ...args: unknown[]) => void;

/**
 * Gets the browser window with optional Ketch SDK.
 * Shared helper used by ketch-consent-api.ts.
 * @returns Window object with optional ketch, or empty object
 */
export function getWindow():
  | (Window & { ketch?: KetchFunction })
  | Record<string, never> {
  return GLOBAL.getWindow() as
    | (Window & { ketch?: KetchFunction })
    | Record<string, never>;
}

// =============================================================================
// TCF item extraction from purposes
// The Ketch getFullConfig() response includes TCF features, special features,
// and special purposes as purpose entries with codes like "tcf_feature_1",
// "tcf_specialFeature_2", etc. — each with name, description, illustrations.
// =============================================================================

/**
 * Extracts TCF features, special features, and special purposes from the
 * expanded Ketch purposes array (getFullConfig response).
 *
 * Ketch now includes these as purpose entries with codes like "tcf_feature_1",
 * "tcf_specialPurpose_2", etc. Each carries name, description, illustrations,
 * and legalBasisCode — eliminating the need for GVL enrichment.
 *
 * @param purposes - Raw purposes array from Ketch getFullConfig()
 * @returns Extracted features, specialFeatures, and specialPurposes
 */
export function extractTCFItemsFromPurposes(purposes: RawKetchPurpose[]): {
  features: KetchTCFFeature[];
  specialFeatures: KetchTCFFeature[];
  specialPurposes: KetchTCFSpecialPurpose[];
} {
  const features: KetchTCFFeature[] = [];
  const specialFeatures: KetchTCFFeature[] = [];
  const specialPurposes: KetchTCFSpecialPurpose[] = [];

  for (const p of purposes) {
    const code = p.code ?? '';
    const item = mapPurposeToTCFItem(code, p);

    if (item && code.startsWith('tcf_feature_')) {
      features.push(item);
    } else if (item && code.startsWith('tcf_specialFeature_')) {
      specialFeatures.push(item);
    } else if (item && code.startsWith('tcf_specialPurpose_')) {
      specialPurposes.push(item);
    }
  }

  return { features, specialFeatures, specialPurposes };
}

/**
 * Parses a TCF purpose entry into a typed item, or returns null if not a TCF item.
 * @param code - Purpose code (e.g. "tcf_feature_1")
 * @param p - Raw purpose entry from Ketch config
 * @returns Typed TCF item or null
 */
function mapPurposeToTCFItem(
  code: string,
  p: RawKetchPurpose,
): KetchTCFFeature | null {
  const isTCFItem =
    code.startsWith('tcf_feature_') ||
    code.startsWith('tcf_specialFeature_') ||
    code.startsWith('tcf_specialPurpose_');
  if (!isTCFItem) return null;

  const parts = code.split('_');
  const id = Number.parseInt(parts[parts.length - 1] ?? '', 10);
  if (Number.isNaN(id)) return null;

  return {
    id,
    name: p.name ?? '',
    legalBasis: p.legalBasisCode,
    description: p.description,
    ...(p.illustrations?.length ? { illustrations: p.illustrations } : {}),
  };
}

// =============================================================================
// Vendor enrichment from systems.tcf[].tcfMetadata
// =============================================================================

/**
 * Result of enriching vendors with TCF metadata.
 * Contains the enriched vendor list plus deduplicated top-level TCF items.
 */
export interface VendorEnrichmentResult {
  vendors: KetchVendor[] | undefined;
  features: KetchTCFFeature[] | undefined;
  specialFeatures: KetchTCFFeature[] | undefined;
  specialPurposes: KetchTCFSpecialPurpose[] | undefined;
}

/**
 * Enriches raw vendors with TCF metadata from systems.tcf and deduplicates
 * features, special features, and special purposes across all TCF vendors.
 *
 * @param rawVendors - Top-level vendors from Ketch full config
 * @param tcfVendors - TCF vendor entries from systems.tcf in Ketch full config
 * @returns Enriched vendors and deduplicated top-level TCF items
 */
export function enrichVendorsWithTCFMetadata(
  rawVendors: RawKetchVendor[] | undefined,
  tcfVendors: RawTCFSystemVendor[],
): VendorEnrichmentResult {
  // Build a lookup from systems.tcf for enrichment
  const tcfMetadataMap = new Map<string, RawTCFVendorMetadata>();
  tcfVendors.forEach((tv) => {
    if (tv.tcfMetadata) {
      tcfMetadataMap.set(tv.id, tv.tcfMetadata);
    }
  });

  // Enrich top-level vendors with TCF metadata
  const enrichedVendors: KetchVendor[] | undefined = rawVendors?.map((v) => {
    const meta = tcfMetadataMap.get(v.id);

    // Without TCF metadata, return base vendor fields only.
    // Raw vendor features/specialFeatures lack numeric IDs so we omit them
    // to avoid violating the KetchTCFFeature contract (which requires id: number).
    if (!meta) {
      return {
        id: v.id,
        name: v.name,
        policyUrl: v.policyUrl,
        purposes: v.purposes,
        legIntPurposes: v.legIntPurposes,
        cookieMaxAgeSeconds: v.cookieMaxAgeSeconds,
        usesCookies: v.usesCookies,
        usesNonCookieAccess: v.usesNonCookieAccess,
      };
    }

    // Enrich purposes with per-purpose retention from TCF metadata
    const enrichedPurposes = meta.purposes
      ? meta.purposes.map((p) => ({
          id: p.id,
          legalBasis: p.legalBasis,
          name: p.name,
          ...(p.retention === undefined ? {} : { retention: p.retention }),
        }))
      : v.purposes;

    return {
      id: v.id,
      name: v.name,
      policyUrl: v.policyUrl,
      // Prefer TCF metadata fields (they have numeric IDs and richer data).
      // Note: specialPurposes/features/specialFeatures/legIntPurposes do NOT fall back
      // to v.* because raw vendor values lack the numeric `id` required by their types,
      // and null from TCF metadata means "explicitly none" (not "use raw fallback").
      purposes: enrichedPurposes,
      specialPurposes: meta.specialPurposes ?? undefined,
      features: meta.features ?? undefined,
      specialFeatures: meta.specialFeatures ?? undefined,
      legIntPurposes:
        meta.legIntPurposes === undefined
          ? v.legIntPurposes
          : (meta.legIntPurposes ?? undefined),
      flexiblePurposes: meta.flexiblePurposes ?? undefined,
      cookieMaxAgeSeconds: meta.cookieMaxAgeSeconds ?? v.cookieMaxAgeSeconds,
      cookieRefresh: meta.cookieRefresh ?? undefined,
      usesCookies: meta.usesCookies ?? v.usesCookies,
      usesNonCookieAccess: meta.usesNonCookieAccess ?? v.usesNonCookieAccess,
      legIntClaim: meta.legIntClaim ?? undefined,
      deviceStorageDisclosureUrl: meta.deviceStorageDisclosure ?? undefined,
      dataDeclaration: meta.dataDeclaration ?? undefined,
      dataRetention: meta.dataRetention ?? undefined,
    };
  });

  // Deduplicate features, special features, special purposes across all TCF vendors
  const featMap = new Map<number, KetchTCFFeature>();
  const sfMap = new Map<number, KetchTCFFeature>();
  const spMap = new Map<number, KetchTCFSpecialPurpose>();

  tcfVendors.forEach((tv) => {
    const meta = tv.tcfMetadata;
    if (!meta) return;

    meta.features?.forEach((f) => {
      if (!featMap.has(f.id)) featMap.set(f.id, f);
    });
    meta.specialFeatures?.forEach((sf) => {
      if (!sfMap.has(sf.id)) sfMap.set(sf.id, sf);
    });
    meta.specialPurposes?.forEach((sp) => {
      if (!spMap.has(sp.id)) spMap.set(sp.id, sp);
    });
  });

  return {
    vendors: enrichedVendors,
    features: featMap.size > 0 ? Array.from(featMap.values()) : undefined,
    specialFeatures: sfMap.size > 0 ? Array.from(sfMap.values()) : undefined,
    specialPurposes: spMap.size > 0 ? Array.from(spMap.values()) : undefined,
  };
}
